import { useMemo, useState } from 'react';
import type { Task, Volunteer } from '@nepal-football/shared';

import { useDeleteVolunteer, useTasks, useVolunteers } from '../api/hooks';
import { useAdminAuth } from '../hooks/AdminAuthContext';
import VolunteerForm from '../components/VolunteerForm';
import VolunteerRow from '../components/VolunteerRow';

const DAY_COLS: { key: 'Friday' | 'Saturday' | 'Sunday'; label: string }[] = [
  { key: 'Friday',   label: 'Fri 3 Jul' },
  { key: 'Saturday', label: 'Sat 4 Jul' },
  { key: 'Sunday',   label: 'Sun 5 Jul' },
];

export default function Volunteers() {
  const { isAdmin } = useAdminAuth();
  const { data, isLoading, isError } = useVolunteers();
  const { data: tasks } = useTasks();
  const deleteMutation = useDeleteVolunteer();

  const tasksById = useMemo(() => {
    const m = new Map<string, Task>();
    for (const t of tasks ?? []) m.set(t.id, t);
    return m;
  }, [tasks]);

  const [formMode, setFormMode] = useState<
    | { kind: 'closed' }
    | { kind: 'create' }
    | { kind: 'edit'; volunteer: Volunteer }
  >({ kind: 'closed' });

  const total    = data?.length ?? 0;
  const friCount = data?.filter((v) => v.days.includes('Friday')).length   ?? 0;
  const satCount = data?.filter((v) => v.days.includes('Saturday')).length ?? 0;
  const sunCount = data?.filter((v) => v.days.includes('Sunday')).length   ?? 0;

  return (
    <div>
      {/* Header */}
      <div className="prep-banner" style={{ marginBottom: 24 }}>
        <div className="prep-banner-icon">🤝</div>
        <div>
          <div className="prep-banner-title">Registered Volunteers</div>
          <div className="prep-banner-sub">
            Nepali Europapokal 2026 · Sportanlage Grüngürtel, Berlin · 3–5 July 2026
          </div>
        </div>
      </div>

      {/* Summary stats */}
      {!isLoading && !isError && total > 0 && (
        <div className="stats-row" style={{ marginBottom: 20 }}>
          {[
            { icon: '🤝', value: total,    label: 'Total Volunteers' },
            { icon: '📅', value: friCount, label: 'Available Friday'   },
            { icon: '⚽', value: satCount, label: 'Available Saturday' },
            { icon: '🏆', value: sunCount, label: 'Available Sunday'   },
          ].map(({ icon, value, label }) => (
            <div key={label} className="stat-card gold">
              <div className="stat-icon">{icon}</div>
              <div className="stat-value">{value}</div>
              <div className="stat-label">{label}</div>
            </div>
          ))}
        </div>
      )}

      <div className="card">
        <div className="card-header-row">
          <div className="card-title">👥 Volunteer List</div>
          {isAdmin && (
            <button
              type="button"
              className="reg-btn reg-btn-compact"
              onClick={() => setFormMode({ kind: 'create' })}
            >
              + Add Volunteer
            </button>
          )}
        </div>

        {isLoading && (
          <div className="inline-loading">Loading volunteers…</div>
        )}

        {isError && (
          <div className="inline-error">
            ⚠️ Volunteer information is unavailable. Please try again later.
          </div>
        )}

        {!isLoading && !isError && total === 0 && (
          <div className="inline-loading">
            No volunteers registered yet.
          </div>
        )}

        {!isLoading && !isError && total > 0 && (
          <div style={{ overflowX: 'auto' }}>
            <table className="vol-table" aria-label="Registered volunteers">
              <thead>
                <tr>
                  <th className="vol-th">Name</th>
                  {DAY_COLS.map(({ key, label }) => (
                    <th key={key} className="vol-th vol-th-day">{label}</th>
                  ))}
                  <th className="vol-th">Tasks</th>
                  <th className="vol-th">City</th>
                  <th className="vol-th">Registered</th>
                  {isAdmin && <th className="vol-th">Actions</th>}
                </tr>
              </thead>
              <tbody>
                {data!.map((v) => (
                  <VolunteerRow
                    key={v.id}
                    volunteer={v}
                    isAdmin={isAdmin}
                    tasksById={tasksById}
                    onEdit={(volunteer) => setFormMode({ kind: 'edit', volunteer })}
                    onDelete={(id) => deleteMutation.mutate(id)}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {formMode.kind !== 'closed' && (
        <div
          className="modal-backdrop"
          role="dialog"
          aria-modal="true"
          aria-label={formMode.kind === 'edit' ? 'Edit volunteer' : 'Add volunteer'}
          onClick={(e) => {
            if (e.target === e.currentTarget) setFormMode({ kind: 'closed' });
          }}
        >
          <div className="modal-box">
            <VolunteerForm
              volunteer={formMode.kind === 'edit' ? formMode.volunteer : undefined}
              onSave={() => setFormMode({ kind: 'closed' })}
              onCancel={() => setFormMode({ kind: 'closed' })}
            />
          </div>
        </div>
      )}
    </div>
  );
}
